import { useEffect, useState } from "react";
import { Hook, Nullable } from "sonobello.utilities.react";

import { DisqualifyingReason } from "../../types/DisqualifyingReason";
import { useObx } from "../../hooks/useObx";
import ApiRequestPaths from "../../constants/ApiRequestPaths";

interface DisqualifyingReasonResponse {
  id: string;
  name: string;
  leadResultTypeId: number;
}

export interface UseGetDisqualifyingReasonsProps {
  /** The id of the contact for which to fetch disqualifying reasons. */
  contactId: string;
  /** When true, prevents the request from being sent. */
  disabled?: boolean;
}

/** A hook which fetches the disqualifying reasons for a given contact.
 * Maps the API response from leadResultTypeId to contactResultTypeId for domain code.
 */
const UseGetDisqualifyingReasons: Hook<DisqualifyingReason[], boolean, UseGetDisqualifyingReasonsProps> = ({
  contactId,
  disabled = false
}) => {
  const { res, err, loading, setReq } = useObx<DisqualifyingReasonResponse[]>("Get Disqualifying Reasons");

  const [result, setResult] = useState<Nullable<DisqualifyingReason[]>>(() => {
    if (!disabled && contactId) {
      setReq(
        r =>
          r && {
            ...r,
            url: ApiRequestPaths.getDisqualifyingReasons(contactId)
          }
      );
    }
    return null;
  });

  useEffect(() => {
    res &&
      setResult(
        res.payload.map(({ leadResultTypeId, ...rest }): DisqualifyingReason => ({
          ...rest,
          contactResultTypeId: leadResultTypeId
        }))
      );
  }, [res]);

  return {
    result,
    error: Boolean(err),
    isLoading: loading
  };
};

export default UseGetDisqualifyingReasons;
